import React from 'react'
import { formatBigMoney, formatNumber, formatPercentage } from '../../packages/utils/numberFormatters'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import { lowercaseFirstLetter } from '../../utils/stringConversion'
import { calculateIssueTotalOfferingSize, getSummarizedFeeLevels } from '../issues/utils'
import { NewsItem } from 'types/NewsItem'

const getTypeName = (issue: NewsItem, t: TFunction) => {
    const { type, earnout } = issue.issueData

    const isEarnoutIssue = type === 'Non-cash issue' && earnout
    const typeName = isEarnoutIssue ? 'tilläggsköpeskilling' : t(`entities.issueType.values.${type}`).toLowerCase()

    return typeName
}

const NoTermsInitially = ({ row }: { row: NewsItem }) => {
    const { t } = useTranslation()
    const companyName = row.companyName
    const typeName = getTypeName(row, t)

    return (
        <span>
            {companyName} offentliggör en {typeName} (villkor kommer senare)
        </span>
    )
}

const GuaranteeFeeIssue = ({ row }: { row: NewsItem }) => {
    const { t } = useTranslation()
    const companyName = row.companyName
    const issueDataType = row.issueData.type

    return (
        <span>
            {companyName} genomför en kvittningsemission avseende{' '}
            {t(`entities.issueType.values.${issueDataType}`).toLowerCase()}
        </span>
    )
}

const ABBDirectedIssue = ({ row }: { row: NewsItem }) => {
    const { t, i18n } = useTranslation()
    const locale = i18n.language

    const companyName = row.companyName
    const issueDataType = row.issueData.type

    const abbTarget = row.issueData.abbTarget
    const totalOfferedUnits = (row.issueData.offeredUnits ?? 0) + (row.issueData.totalOfferedUnits ?? 0)
    const currency = row.currency

    return (
        <>
            <span>
                {companyName} offentliggör en {lowercaseFirstLetter(t(`entities.issueType.values.${issueDataType}`))}
            </span>
            {abbTarget && <span> om cirka {formatBigMoney(abbTarget, { locale, currencySymbol: currency })}</span>}
            {!abbTarget && totalOfferedUnits && (
                <span> om cirka {formatNumber(totalOfferedUnits, { locale, decimals: 0 })} aktier</span>
            )}
        </>
    )
}

const IPOIssue = ({ row }: { row: NewsItem }) => {
    const { i18n } = useTranslation()
    const locale = i18n.language

    const companyName = row.companyName
    const currency = row.currency
    const { offeredUnits, offeredUnitsUnlisted, secondaryOffering, ipoIntervalLow, ipoIntervalHigh } = row.issueData
    const totalOfferedUnits =
        offeredUnits === null && offeredUnitsUnlisted === null
            ? null
            : (offeredUnits ?? 0) + (offeredUnitsUnlisted ?? 0)

    const title = (() => {
        if (row.issueData.unitSubPrice !== null) {
            const unitSubPrice = parseFloat(row.issueData.unitSubPrice)
            return `${companyName} offentliggör detaljer inför en IPO om cirka ${formatBigMoney(
                (totalOfferedUnits + secondaryOffering) * unitSubPrice,
                { locale, currencySymbol: currency },
            )}`
        } else if (!row.issueData.unitSubPrice && ipoIntervalLow && ipoIntervalHigh) {
            return `${companyName} offentliggör detaljer inför en IPO om cirka ${formatBigMoney(
                (totalOfferedUnits + secondaryOffering) * ipoIntervalLow,
                { locale, currencySymbol: currency },
            )} - ${formatBigMoney((totalOfferedUnits + secondaryOffering) * ipoIntervalHigh, {
                locale,
                currencySymbol: currency,
            })}`
        }
    })()

    return <span>{title}</span>
}

const WarrantIssue = ({ row }: { row: NewsItem }) => {
    const { warrantData } = row

    return (
        <span>
            {`${row.companyName} emitterar ${warrantData.unlisted ? 'onoterade ' : ''}teckningsoptioner av serie ${row.name || warrantData.name}`}
        </span>
    )
}

const ConvertibleIssue = ({ row }: { row: NewsItem }) => {
    const { i18n } = useTranslation()
    const locale = i18n.language

    const { convertibleData } = row

    console.log('convertibleData: ', convertibleData)

    if (convertibleData.creditLine !== null) {
        return (
            <span>
                {`${row.companyName} ingår en kreditfacilitet med konvertibla lån om ${formatBigMoney(convertibleData.creditLine, { locale, currencySymbol: row.currency })}`}
            </span>
        )
    }

    if (convertibleData.loanAmount !== null) {
        return (
            <span>
                {`${row.companyName} upptar ett konvertibellån om ${formatBigMoney(convertibleData.loanAmount, { locale, currencySymbol: row.currency })}`}
            </span>
        )
    }
}

const Issue = ({ row }: { row: NewsItem }) => {
    const { issueData, currency, companyName } = row
    const { type, sharesInUnit, hasWarrants, investors } = issueData

    const investorsExcludingSellers = investors.filter((investor) => !investor.isSeller)
    const totalOfferedUnits =
        issueData.offeredUnits === null && issueData.offeredUnitsUnlisted === null
            ? null
            : (issueData.offeredUnits ?? 0) + (issueData.offeredUnitsUnlisted ?? 0)
    const { totalOfferingSize } = calculateIssueTotalOfferingSize({
        ...row.issueData,
        totalOfferedUnits,
    })
    const summarizedFeeLevels = getSummarizedFeeLevels(investorsExcludingSellers, totalOfferingSize)

    const { t, i18n } = useTranslation()
    const locale = i18n.language
    const typeName = getTypeName(row, t)

    const amount =
        (issueData.abbTarget ?? 0) +
        ((issueData.offeredUnits ?? 0) + (issueData.offeredUnitsUnlisted ?? 0)) * issueData.unitSubPrice

    const totalCommitment = summarizedFeeLevels.reduce((acc, fee) => {
        return acc + fee.amountInPercentage
    }, 0)

    const rightsIssueText = (() => {
        let text = ''

        if (amount !== 0) {
            if (sharesInUnit === 0 && hasWarrants) {
                text += ' med enbart teckningsoptioner'
            } else {
                text += ` om cirka
                    ${formatBigMoney(amount, {
                        locale,
                        currencySymbol: currency,
                    })}`
            }
        }
        text += ` säkerställd till ${formatPercentage(Math.min(totalCommitment, 100), { locale, decimals: 0 })}`

        return text
    })()

    return (
        <span>
            {companyName} offentliggör en {typeName}
            {type === 'Rights issue' ? rightsIssueText : null}
            {type !== 'Rights issue' &&
                amount !== 0 &&
                ' om cirka ' +
                    formatBigMoney(amount, {
                        locale,
                        currencySymbol: currency,
                    })}
            {type === 'Non-cash issue' && ' avseende ' + issueData.noncashConsideration}
        </span>
    )
}

function NewsHeadlinePublication({ row }: { row: NewsItem }) {
    const { issueData } = row

    if (row.entityType === 'Warrant') return <WarrantIssue row={row} />
    if (row.entityType === 'Convertible') return <ConvertibleIssue row={row} />
    else if (issueData.type === 'Guarantor fee issue') return <GuaranteeFeeIssue row={row} />
    else if (issueData.type === 'ABB Directed issue') return <ABBDirectedIssue row={row} />
    else if (issueData.type === 'IPO') return <IPOIssue row={row} />
    else if (issueData.noTermsInitially && issueData.abbTarget === null) return <NoTermsInitially row={row} />
    else return <Issue row={row} />
}

export default NewsHeadlinePublication
